/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo } from 'react';

import { CSSObject } from 'styled-components';

import {
  Table,
  TableBodyRow,
  TableBodyCell,
  TABLE_SIZES,
  TableHeaderRow,
  actions,
} from '@adsk/alloy-react-table-v2';
import theme from '@adsk/alloy-react-theme';

import { useScheduleActivityColumns } from './useScheduleActivitiesColumns';
import { listToTree } from '../../helpers/utils';
import {
  ActivityRow,
  ActivityCode,
  ScheduleActivityReferencePickerPlugin,
} from '../../types';
import { useScheduleContext } from '../ScheduleProvider';
import { ACTIONS } from '../ScheduleProvider/ScheduleProvider';

const defaultColumnOrder = [
  'checkbox',
  'id',
  'fileActivityId',
  'name',
  'startDate',
  'progress',
  'endDate',
  'duration',
  'remainingDuration',
  'resources',
];

const getRowId = (activity: ActivityRow) => activity.id;

const mapifyArray = (arr: string[]) =>
  arr.reduce<Record<string, true>>((map, item) => {
    map[item] = true;
    return map;
  }, {});

const ScheduleCustomDataView: Exclude<
  ScheduleActivityReferencePickerPlugin['CustomDataView'],
  undefined
> = ({
  entities,
  disabledIds,
  selectedIds,
  onChange,
  setSelection,
  loading,
}) => {
  const activityCodes = useMemo(() => {
    return Array.from(
      entities
        .flatMap((entity) => entity.activity?.activityCodes)
        .reduce(
          (columns, activityCode: ActivityCode) =>
            columns.add(activityCode.name),
          new Set<string>(),
        ),
    ).sort((a, b) => a.localeCompare(b));
  }, [entities]);

  const toggleRowSelected = useCallback(
    (id: string, checked: boolean) => {
      const entity = entities.find((entity) => entity.id === id);
      if (entity) {
        onChange({ entity, checked });
      }
    },
    [entities, onChange],
  );

  const {
    laborResources,
    activityCodesMap,
    setLaborResources,
    setActivityCodesMap,
    collapsedRows,
    hiddenColumns,
    columnOrder,
    dispatch,
  } = useScheduleContext();

  useEffect(() => {
    // This is a hack to be able to access the setSelection function in the TopBar component
    dispatch({ type: ACTIONS.UPDATE_SET_SELECTION, payload: setSelection });
  }, [setSelection, dispatch]);

  const columns = useScheduleActivityColumns(activityCodes, toggleRowSelected);

  const selectedRowIds = useMemo(() => mapifyArray(selectedIds), [selectedIds]);

  const data = useMemo(
    () =>
      listToTree(
        entities.map((item) => ({
          ...item,
          expanded: !collapsedRows.get(item.id),
          disabled: disabledIds.includes(item.id),
        })) as unknown as ActivityRow[],
      ),
    [entities, collapsedRows, disabledIds],
  );

  useEffect(() => {
    if (!loading && entities?.length) {
      if (!hiddenColumns) {
        const hiddenColumns = new Set([
          ...activityCodes,
          'fileActivityId',
          'remainingDuration',
          'resources',
        ]);
        dispatch({
          type: ACTIONS.UPDATE_HIDDEN_COLUMNS,
          payload: hiddenColumns,
        });
      }

      if (!columnOrder) {
        dispatch({
          type: ACTIONS.UPDATE_COLUMN_ORDER,
          payload: defaultColumnOrder,
        });
      }

      if (laborResources.length === 0) {
        const laborResourcesOptions = Array.from(
          new Set(
            entities.reduce(
              (names: string[], entity) =>
                names.concat(
                  entity.activity.assignees.map((assignee) => assignee.name),
                ),
              [],
            ),
          ),
        ).map((name) => ({ label: name, value: name }));
        setLaborResources(laborResourcesOptions);
      }

      if (activityCodesMap.size === 0) {
        const activityCodesMapOptions = entities
          .flatMap((entity) => entity.activity.activityCodes)
          .filter(Boolean)
          .reduce(
            (map: Map<string, Set<string>>, { name, value }) =>
              map.set(name, (map.get(name) || new Set()).add(value)),
            new Map(),
          );

        setActivityCodesMap(activityCodesMapOptions);
      }
    }
  }, [
    loading,
    entities,
    activityCodes,
    setActivityCodesMap,
    setLaborResources,
    activityCodesMap.size,
    laborResources.length,
    dispatch,
    columnOrder,
    hiddenColumns,
  ]);

  const handleTableAction = useCallback(
    ({ action }: { action: any }) => {
      if (action.type === actions.toggleHideColumn && hiddenColumns) {
        // action.value is undefined, in which you would expect to return the toggle state
        // But unfortunately it's undefined so we need to figure it out on our own
        const newHiddenColumns = new Set(Array.from(hiddenColumns));
        if (hiddenColumns.has(action.columnId)) {
          newHiddenColumns.delete(action.columnId);
        } else {
          newHiddenColumns.add(action.columnId);
        }
        dispatch({
          type: ACTIONS.UPDATE_HIDDEN_COLUMNS,
          payload: newHiddenColumns,
        });
      }
      if (action.type === actions.setColumnOrder) {
        dispatch({
          type: ACTIONS.UPDATE_COLUMN_ORDER,
          payload: action.columnOrder,
        });
      }
      if (action.type === actions.toggleHideAllColumns) {
        dispatch({
          type: ACTIONS.UPDATE_HIDDEN_COLUMNS,
          payload: new Set([
            ...activityCodes,
            'fileActivityId',
            'remainingDuration',
            'resources',
          ]),
        });
      }
    },
    [hiddenColumns, dispatch, activityCodes],
  );

  const arrHiddenColumns = useMemo(() => {
    return Array.from(hiddenColumns ?? []);
  }, [hiddenColumns]);

  return (
    <Table<ActivityRow>
      key="0"
      columns={columns as any}
      data={data}
      isExpandable
      isMultiSelectable
      // isColumnReorderable
      isResizable
      isSortable
      hasVirtualList
      hasStickyHeader
      defaultSize={TABLE_SIZES.SMALL}
      style={{
        '[data-sticky-td]': {
          boxShadow: 'none',
          justifyContent: 'flex-end',
          position: 'sticky',
        },
      }}
      setTableOptions={(opts) => ({
        ...opts,
        getRowId,
        autoResetHiddenColumns: false,
        autoResetSelectedRows: false,
        autoResetExpanded: false,
        initialState: {
          ...opts.initialState,
          hiddenColumns: arrHiddenColumns,
          columnOrder,
        },
        useControlledState: (state) => {
          if (opts) {
            return (opts as any).useControlledState({
              ...state,
              hiddenColumns: arrHiddenColumns,
              columnOrder,
            });
          }
          return {};
        },
      })}
      selectedRowIds={selectedRowIds}
      setPluginHooks={(hooks) =>
        hooks.filter(({ pluginName }) => pluginName !== 'useMultiSelectColumn')
      }
      // storageKey="acs-schedule-reference-picker-table"
      onAction={handleTableAction}
      renderHeaderRow={({ style, ...props }) => {
        return (
          <TableHeaderRow
            {...props}
            style={
              { ...(style as CSSObject), ...theme.typography.heading5 } as any
            }
          />
        );
      }}
      renderBodyRow={({ style, row, ...props }) => {
        return (
          <TableBodyRow {...props} row={row} style={style}>
            {row.cells.map((cell) => {
              const { style, ...cellProps } = cell.getCellProps({});
              return (
                <TableBodyCell
                  {...cellProps}
                  style={{
                    ...style,
                    ...theme.typography.labelSmall,
                    color: theme.colors.charcoal700,
                  }}
                  key={cellProps.key}
                  cell={cell}
                />
              );
            })}
          </TableBodyRow>
        );
      }}
    />
  );
};

export default ScheduleCustomDataView;
